import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { contactInfo } from '../mock';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationLinks = [
    { name: 'About', href: '/#about' },
    { name: 'Services', href: '/#services' },
    { name: 'Process', href: '/#process' },
    { name: 'Pricing', href: '/#pricing' },
    { name: 'FAQs', href: '/#faqs' },
    { name: 'Contact', href: '/#contact' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EFEBE9' }}>
      <Helmet>
        <title>Privacy Policy - TradeMark Registry</title>
        <meta name="description" content="Privacy Policy for TradeMark Registry trademark registration services. Learn how we collect, use, and protect your personal information in compliance with privacy regulations." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://trademark-pro-7.preview.emergentagent.com/privacy" />
      </Helmet>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#3E2723] shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo-white.png" 
                alt="TradeMark Registry Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-[#D7CCC8] hover:text-white transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={() => navigate('/')}
                className="bg-[#8D6E63] hover:bg-[#6D4C41] text-white"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-4">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-2 text-[#D7CCC8] hover:text-white transition-colors duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={() => {
                  navigate('/');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full mt-2 bg-[#8D6E63] hover:bg-[#6D4C41] text-white"
              >
                Get Started
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl" style={{ marginTop: '64px' }}>
        <h1 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-8">Privacy Policy</h1>
        
        <Card className="bg-white border-[#D7CCC8]">
          <CardContent className="p-8 md:p-12">
            <p className="text-[#8D6E63] mb-6">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-8 text-[#8D6E63]">
              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">1. Introduction</h2>
                <p className="mb-4">
                  {contactInfo.businessName} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                  explains how we collect, use, disclose, and safeguard your information when you visit our website or use our 
                  trademark registration services.
                </p>
                <p>
                  Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, 
                  please do not access the site or use our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">2. Information We Collect</h2>
                <h3 className="text-xl font-semibold text-[#3E2723] mb-3">2.1 Personal Information</h3>
                <p className="mb-4">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Register for our services</li>
                  <li>Fill out contact forms</li>
                  <li>Request a consultation</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Communicate with us</li>
                </ul>
                <p className="mb-4">
                  This information may include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name and contact information (email address, phone number, mailing address)</li>
                  <li>Business information (company name, business type, industry)</li>
                  <li>Trademark details (proposed marks, goods/services descriptions)</li>
                  <li>Payment information (processed securely through third-party payment processors)</li>
                  <li>Any other information you choose to provide</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3E2723] mb-3 mt-6">2.2 Automatically Collected Information</h3>
                <p className="mb-4">
                  When you visit our website, we may automatically collect certain information about your device and browsing actions, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">3. How We Use Your Information</h2>
                <p className="mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide, operate, and maintain our trademark registration services</li>
                  <li>Process your trademark applications and related services</li>
                  <li>Communicate with you about your trademark matters</li>
                  <li>Send you service-related notices and updates</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations and USPTO requirements</li>
                  <li>Prevent fraud and enhance security</li>
                  <li>Send marketing communications (with your consent)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">4. Information Sharing and Disclosure</h2>
                <p className="mb-4">
                  We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>USPTO:</strong> Trademark application information is submitted to the United States Patent and Trademark Office and becomes part of the public record</li>
                  <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf (e.g., payment processors, email services)</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government regulation</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of our business assets</li>
                  <li><strong>With Your Consent:</strong> We may share information with your explicit permission</li>
                </ul>
                <p className="mt-4">
                  We do not sell your personal information to third parties.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information. 
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to 
                  protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">6. Cookies and Tracking Technologies</h2>
                <p className="mb-4">
                  We use cookies and similar tracking technologies to enhance your experience on our website. You can control 
                  cookie settings through your browser preferences. However, disabling cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">7. Your Privacy Rights</h2>
                <p className="mb-4">
                  Depending on your location, you may have certain rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access and receive a copy of your personal information</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Request deletion of your information (subject to legal obligations)</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Object to or restrict certain processing activities</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">8. Children's Privacy</h2>
                <p>
                  Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
                  information from children. If you believe we have collected information from a child, please contact us 
                  immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">9. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of 
                  these external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">10. Updates to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. The updated version will be indicated by the "Last Updated" 
                  date at the top of this policy. We encourage you to review this Privacy Policy periodically for any changes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-[#3E2723] mb-4">11. Contact Us</h2>
                <p className="mb-4">
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-[#EFEBE9] p-6 rounded-lg">
                  <p className="mb-2"><strong>{contactInfo.businessName}</strong></p>
                  <p className="mb-2">{contactInfo.address}</p>
                  <p className="mb-2">Email: {contactInfo.email}</p>
                  <p>Phone: {contactInfo.phone}</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 mt-12" style={{ backgroundColor: '#3E2723' }}>
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-[#D7CCC8] text-sm">
            © {new Date().getFullYear()} {contactInfo.businessName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
